import {
  AfterViewInit,
  Directive,
  DoCheck,
  Injector,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import {
  BehaviorSubject,
  Subject,
  filter,
  map,
  merge,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { FormFieldControlDirective } from '../form-field';
import { SignaturePadComponent } from './signature-pad.component';

@Directive({
  selector: '[luiSignaturePadDatuUrlControl]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SignaturePadDatuUrlControlDirective,
    },
    {
      provide: FormFieldControlDirective,
      useExisting: SignaturePadDatuUrlControlDirective,
    },
  ],
})
export class SignaturePadDatuUrlControlDirective
  implements
    ControlValueAccessor,
    FormFieldControlDirective<unknown>,
    OnInit,
    AfterViewInit,
    DoCheck,
    OnDestroy
{
  onChange!: (value: unknown) => void;

  onTouched!: () => void;

  touched = false;

  disabled = false;

  ngControl!: NgControl | null;

  private _injector = inject(Injector);

  private signaturePad$ = new BehaviorSubject<SignaturePadComponent | null>(
    null
  );

  private valueRead$ = new BehaviorSubject<string>('');

  private destroy$ = new Subject<void>();

  readonly stateChanges: Subject<void> = new Subject<void>();

  readonly errorState!: boolean;
  // TODO: implement below for complete functionality
  focused: boolean = false;
  empty: boolean = false;
  required: boolean = false;

  get value() {
    return this.signaturePad$.value?.toDataUrl() ?? '';
  }

  constructor(signaturePad: SignaturePadComponent) {
    // Access the DOM element using this.elementRef.nativeElement
    // Add your directive logic here
    this.signaturePad$.next(signaturePad);
  }

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
    });
  }

  ngAfterViewInit(): void {
    this.handleReadStream();
    this.hanndleWriteStream();
  }

  ngDoCheck(): void {
    if (this.ngControl && this.ngControl.touched !== this.touched) {
      this.stateChanges.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: unknown) {
    if (typeof value !== 'string') return;
    this.valueRead$.next(value);
  }

  registerOnChange(onChange: () => void) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }

  private handleReadStream() {
    this.signaturePad$
      .pipe(
        startWith(this.signaturePad$.value),
        filter(Boolean),
        switchMap((signaturePad) =>
          this.valueRead$.pipe(
            startWith(this.valueRead$.value),
            tap((value) => {
              if (!value) {
                signaturePad.clear();
                return;
              }
              signaturePad.fromDataUrl(value);
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private hanndleWriteStream() {
    this.signaturePad$
      .pipe(
        startWith(this.signaturePad$.value),
        filter(Boolean),
        switchMap((signaturePad) =>
          merge(
            signaturePad.drawEnd.pipe(
              map(() => ({ signaturePad, eventType: 'write' }))
            ),
            signaturePad.cleared.pipe(
              map(() => ({ signaturePad, eventType: 'clear' }))
            )
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ signaturePad, eventType }) => {
        if (eventType === 'write') {
          this.onChange(signaturePad.toDataUrl());
        } else {
          this.onChange('');
        }
        this.touched = true;
        this.onTouched();
        this.stateChanges.next();
      });
  }
}
